import React from 'react';
import { Button, Form, Input } from 'antd';
import { useTranslation } from 'react-i18next';
import { useResolveWecomGroup } from '../graphql/queries/resolveWecomGroup';
import { WecomGroupItem, createEmptyWecomGroupItem, toResolvedPatch } from './wecomGroups';

interface WecomGroupSectionProps {
    items: WecomGroupItem[];
    /** 当前客户ID，新建客户时为空（此时任何绑定都算其他客户） */
    channelId?: number | null;
    onChange: React.Dispatch<React.SetStateAction<WecomGroupItem[]>>;
    readOnly?: boolean;
    /** 提交中时跳过失焦查询，由提交逻辑统一校验 */
    isSubmittingRef: React.MutableRefObject<boolean>;
}

/**
 * 客户信息抽屉「联系人信息」tab 中的企微群信息模块。
 * 新增 / 删除只改本地状态，点击抽屉「确认」后随扩展信息一起保存。
 */
const WecomGroupSection: React.FC<WecomGroupSectionProps> = ({ items, channelId, onChange, readOnly = false, isSubmittingRef }) => {
    const { t } = useTranslation();
    const [resolveWecomGroup] = useResolveWecomGroup();

    const updateItem = (key: string, patch: Partial<WecomGroupItem>) => {
        onChange((prev) => prev.map((item) => (item.key === key ? { ...item, ...patch } : item)));
    };

    const handleBlur = async (item: WecomGroupItem, e: React.FocusEvent<HTMLInputElement>) => {
        if (isSubmittingRef.current) {
            return;
        }
        // 焦点移到抽屉底部按钮（确认 / 取消）时跳过，由提交逻辑统一校验
        const relatedTarget = e.relatedTarget as HTMLElement | null;
        if (relatedTarget?.closest('.ant-drawer-footer')) {
            return;
        }
        const groupId = e.target.value.trim();
        if (!groupId) {
            updateItem(item.key, { groupId, groupName: '', errorKey: undefined, errorParams: undefined });
            return;
        }
        try {
            // 已绑定其他客户、查不到群名称时卡片标红并提示
            const { data } = await resolveWecomGroup({ variables: { groupId, channelId } });
            updateItem(item.key, { groupId, ...toResolvedPatch(data?.resolveWecomGroup) });
        } catch (error) {
            // 查询失败时不打断输入，提交时会再校验一次
            console.error('查询企微群名称失败:', error);
        }
    };

    return (
        <div className="border rounded p-4 bg-gray-50 space-y-4 mb-6">
            <div className="flex items-center justify-between">
                <span className="font-semibold">
                    {t('central.channels.contact.wecomGroupTitle', { count: items.length })}
                </span>
                {!readOnly && (
                    <Button type="link" onClick={() => onChange((prev) => [...prev, createEmptyWecomGroupItem()])}>
                        {t('central.channels.contact.addWecomGroup')}
                    </Button>
                )}
            </div>
            {items.length === 0 && (
                <div className="text-gray-400">{t('central.channels.contact.noWecomGroup')}</div>
            )}
            {items.map((item) => (
                <div
                    key={item.key}
                    data-testid="wecom-group-card"
                    className={`border border-dashed rounded-md p-3 space-y-3 bg-white ${
                        item.errorKey ? 'border-red-500' : 'border-gray-300'
                    }`}
                >
                    <Form.Item label={t('central.channels.contact.wecomGroupId')} colon={false}>
                        <Input
                            value={item.groupId}
                            disabled={item.saved || readOnly}
                            status={item.errorKey ? 'error' : undefined}
                            onChange={(e) =>
                                updateItem(item.key, {
                                    groupId: e.target.value,
                                    groupName: '',
                                    errorKey: undefined,
                                    errorParams: undefined,
                                })
                            }
                            onBlur={(e) => handleBlur(item, e)}
                        />
                    </Form.Item>
                    <Form.Item label={t('central.channels.contact.wecomGroupName')} colon={false}>
                        <Input value={item.groupName} disabled />
                    </Form.Item>
                    {item.errorKey && <div className="text-red-500 text-sm">{String(t(item.errorKey, item.errorParams ?? {}))}</div>}
                    {!readOnly && (
                        <div className="text-right">
                            <Button
                                danger
                                type="link"
                                onClick={() => onChange((prev) => prev.filter((g) => g.key !== item.key))}
                            >
                                {t('central.channels.contact.removeWecomGroup')}
                            </Button>
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
};

export default WecomGroupSection;
